// /API/studyExport 응답(원천 SP ①~④ · 설문 ⑰⑱)을 템플릿이 찾기 쉬운 형태로 한 번만 인덱싱한다.
// 렌더링마다 배열을 find() 로 뒤지면 대상자·열이 늘 때 O(n²) 이 된다.
//
// 방문 키
//   템플릿 visits[] 의 { key, regimen, cycle } 로 대상자 방문(VisitType + VisitCycle)을 키에 대응시킨다.
//   repeat 방문(UV)은 회차별로 "UV#1", "UV#2" … 키를 쓴다.

import { answerValue, buildSurveyCatalog, surveyRef } from './surveyItems';
import { buildCriteriaCatalog, criteriaKey } from './criteriaItems';

export const repeatVisitKey = (key, seq) => `${key}#${seq}`;

const toNumberOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// bit/boolean 이 JDBC·직렬화 경로에 따라 true / 1 / '1' 로 올 수 있다.
export const isTruthyFlag = (v) => v === true || v === 'true' || Number(v) === 1;

/**
 * @param {object} source  { Dictionary, Subjects, Results, DiseaseStatus, SurveyDictionary, Surveys }
 * @param {object} template { visits: [...] }
 * @param {object} [options] { lockedOnly }
 */
export function buildSourceIndex(source, template, options = {}) {
  const visits = template?.visits ?? [];
  const regularByCode = new Map();   // "SCR|0" → key
  const repeatByRegimen = new Map(); // "UV" → key
  for (const v of visits) {
    if (v.repeat) repeatByRegimen.set(v.regimen, v.key);
    else regularByCode.set(`${v.regimen}|${v.cycle ?? 0}`, v.key);
  }

  const visitKeyOf = (visitType, visitCycle, uvSeq) => {
    const repeatKey = repeatByRegimen.get(visitType);
    if (repeatKey) return uvSeq ? repeatVisitKey(repeatKey, uvSeq) : null;
    return regularByCode.get(`${visitType}|${toNumberOrNull(visitCycle) ?? 0}`) ?? null;
  };

  // ── 대상자 ──
  const subjects = (source?.Subjects ?? []).map((s) => ({ ...s, SubjectID: Number(s.SubjectID) }));
  const subjectById = new Map(subjects.map((s) => [s.SubjectID, s]));

  // ── 결과값 · 방문일 ──
  const results = new Map();        // SubjectID → Map("visitKey|TestCode" → row)
  const visitDates = new Map();     // SubjectID → Map(visitKey → VisitDate)
  const repeatCounts = new Map();   // repeat key → Map(SubjectID → 최대 회차)
  const subjectVisits = new Map();  // SubjectID → Map(visitKey → { visitKey, baseKey, seq }) — 방문별 표의 행
  const longRows = [];              // 원자료 표의 행 { subjectId, visitKey, row }
  const stats = { lockedBlanked: 0, resultDuplicates: 0, unmappedVisits: new Set() };

  // 방문 정렬 순서: 템플릿 visits 순서 → 반복 방문은 회차 순
  const visitOrder = new Map(visits.map((v, i) => [v.key, i]));
  const labelOf = new Map(visits.map((v) => [v.key, v.label ?? v.key]));

  // 대상자 방문 기록 (방문일 · 방문별 표의 행 · 반복 방문 회차). 결과(③)와 설문(⑱)이 같이 쓴다.
  // 방문 키를 돌려준다 (템플릿 방문에 없으면 null).
  const noteVisit = (row) => {
    const subjectId = Number(row.SubjectID);
    const uvSeq = toNumberOrNull(row.UVSeq);
    const key = visitKeyOf(row.VisitType, row.VisitCycle, uvSeq);
    if (!key) {
      stats.unmappedVisits.add(`${row.VisitType}${row.VisitCycle ?? ''}`);
      return null;
    }

    if (!visitDates.has(subjectId)) visitDates.set(subjectId, new Map());
    if (row.VisitDate) visitDates.get(subjectId).set(key, row.VisitDate);

    const repeatKey = repeatByRegimen.get(row.VisitType);
    if (!subjectVisits.has(subjectId)) subjectVisits.set(subjectId, new Map());
    if (!subjectVisits.get(subjectId).has(key)) {
      subjectVisits.get(subjectId).set(key, { visitKey: key, baseKey: repeatKey ?? key, seq: repeatKey ? uvSeq : null });
    }
    if (repeatKey && uvSeq) {
      if (!repeatCounts.has(repeatKey)) repeatCounts.set(repeatKey, new Map());
      const m = repeatCounts.get(repeatKey);
      m.set(subjectId, Math.max(m.get(subjectId) ?? 0, uvSeq));
    }
    return key;
  };

  for (const row of source?.Results ?? []) {
    const subjectId = Number(row.SubjectID);
    const key = noteVisit(row);
    if (!key) continue;

    if (!row.TestCode) continue;
    if (Number(row.ResultDupCnt) >= 2) stats.resultDuplicates++;

    let value = row;
    if (options.lockedOnly && !isTruthyFlag(row.IsLock) && isTruthyFlag(row.HasValue)) {
      // 확정 데이터만: 미확정 값은 공란 + 건수만 센다 (행은 유지)
      value = { ...row, Value1: null, Value2: null, HasValue: 0 };
      stats.lockedBlanked++;
    }

    if (!results.has(subjectId)) results.set(subjectId, new Map());
    results.get(subjectId).set(`${key}|${row.TestCode}`, value);
    longRows.push({ subjectId, visitKey: key, row: value });
  }

  // ── 설문 (⑱) ──
  //   답은 설문 1건 = AnswersJSON 하나. 보기 문항은 번호로 바꿔 내보낸다 (surveyItems.answerValue).
  //   원자료 표에는 문항 하나 = 행 하나로 넣는다 (항목코드 "PSQI.Q1").
  const surveyCatalog = buildSurveyCatalog(source?.SurveyDictionary);
  const surveys = new Map(); // SubjectID → Map("visitKey|SurveyCode" → answers)
  const parseAnswers = (v) => {
    if (!v) return {};
    if (typeof v === 'object') return v;
    try { return JSON.parse(v) ?? {}; } catch { return {}; }
  };
  const hasAnswer = (v) => v !== null && v !== undefined && String(v).trim() !== '';
  for (const row of source?.Surveys ?? []) {
    const subjectId = Number(row.SubjectID);
    const key = noteVisit(row);
    if (!key || !row.SurveyCode) continue;

    let answers = parseAnswers(row.AnswersJSON);
    if (options.lockedOnly && !isTruthyFlag(row.IsLock) && Object.values(answers).some(hasAnswer)) {
      answers = {};
      stats.lockedBlanked++;
    }
    if (!surveys.has(subjectId)) surveys.set(subjectId, new Map());
    surveys.get(subjectId).set(`${key}|${row.SurveyCode}`, answers);

    const survey = surveyCatalog.surveys.find((x) => x.surveyCode === row.SurveyCode);
    const itemKeys = survey ? survey.items.map((x) => x.item) : Object.keys(answers);
    for (const itemKey of itemKeys) {
      if (!(itemKey in answers)) continue;
      const entry = surveyCatalog.byRef.get(surveyRef(row.SurveyCode, itemKey));
      const raw = answers[itemKey];
      longRows.push({
        subjectId,
        visitKey: key,
        row: {
          TestCode: `${row.SurveyCode}.${itemKey}`,
          TestNameLc: `${survey?.name ?? row.SurveyCode} ${entry?.name ?? itemKey}`,
          Value1: answerValue(raw, entry),
          Value2: null,
          Unit: null,
          IsLock: row.IsLock,
          HasValue: hasAnswer(raw) ? 1 : 0,
        },
      });
    }
  }

  // ── 선정/제외 기준 (⑳) — 대상자 단위 ──
  const criteriaCatalog = buildCriteriaCatalog(source?.CriteriaDictionary);
  const criteria = new Map(); // SubjectID → Map("I1" → CResult)
  for (const row of source?.Criteria ?? []) {
    const subjectId = Number(row.SubjectID);
    if (!criteria.has(subjectId)) criteria.set(subjectId, new Map());
    criteria.get(subjectId).set(criteriaKey(row.CriteriaType, row.CriteriaNo), row.CResult);
  }

  const visitLabelOf = (visitKey) => {
    const [base, seq] = String(visitKey).split('#');
    return seq ? `${labelOf.get(base) ?? base} ${seq}` : (labelOf.get(base) ?? base);
  };
  const sortVisits = (list) =>
    [...list].sort((a, b) => (visitOrder.get(a.baseKey) ?? 999) - (visitOrder.get(b.baseKey) ?? 999) || (a.seq ?? 0) - (b.seq ?? 0));

  // ── 반복 블록 ──
  const blocks = new Map(); // blockName → Map(SubjectID → Map(visitKey → rows[] (Seq 순)))
  const addBlock = (name, rows) => {
    const byBlock = new Map();
    for (const row of rows ?? []) {
      const subjectId = Number(row.SubjectID);
      const key = visitKeyOf(row.VisitType, row.VisitCycle, null);
      if (!key) continue;
      if (options.lockedOnly && !isTruthyFlag(row.IsLock)) {
        stats.lockedBlanked++;
        continue;
      }
      if (!byBlock.has(subjectId)) byBlock.set(subjectId, new Map());
      const byVisit = byBlock.get(subjectId);
      if (!byVisit.has(key)) byVisit.set(key, []);
      byVisit.get(key).push(row);
    }
    for (const byVisit of byBlock.values()) {
      for (const list of byVisit.values()) list.sort((a, b) => Number(a.Seq) - Number(b.Seq));
    }
    blocks.set(name, byBlock);
  };
  addBlock('diseaseStatus', source?.DiseaseStatus);

  // ── 사전 (열 존재 확인용) ──
  const dictionaryTestCodes = new Set((source?.Dictionary ?? []).map((d) => d.TestCode).filter(Boolean));
  // 설문만 있는 방문도 연구 방문이다
  const dictionaryVisits = new Set([...(source?.Dictionary ?? []), ...(source?.SurveyDictionary ?? [])]
    .map((d) => `${d.RegimenCode}|${toNumberOrNull(d.Cycle) ?? 0}`));

  return {
    subjects,
    subjectById,
    getResult: (subjectId, visitKey, testCode) => results.get(subjectId)?.get(`${visitKey}|${testCode}`) ?? null,
    getVisitDate: (subjectId, visitKey) => visitDates.get(subjectId)?.get(visitKey) ?? null,
    // 설문 답 (보기 문항은 번호). 답이 없으면 ''
    getSurveyAnswer: (subjectId, visitKey, surveyCode, itemKey) => {
      const answers = surveys.get(subjectId)?.get(`${visitKey}|${surveyCode}`);
      if (!answers || !(itemKey in answers)) return '';
      return answerValue(answers[itemKey], surveyCatalog.byRef.get(surveyRef(surveyCode, itemKey)));
    },
    surveyCatalog,
    // 선정/제외 기준 답 (1 예 · 0 아니요). 답이 없으면 ''
    getCriteria: (subjectId, key) => {
      const v = criteria.get(subjectId)?.get(String(key).toUpperCase());
      return v === null || v === undefined ? '' : Number(v);
    },
    criteriaCatalog,
    getBlockRows: (blockName, subjectId, visitKey) => blocks.get(blockName)?.get(subjectId)?.get(visitKey) ?? [],
    // 방문별 표: 대상자가 실제로 가진 방문 (템플릿 방문 순 · 반복 방문은 회차 순)
    getSubjectVisits: (subjectId) =>
      sortVisits(subjectVisits.get(subjectId)?.values() ?? []).map((v) => ({
        ...v,
        visitLabel: visitLabelOf(v.visitKey),
        visitDate: visitDates.get(subjectId)?.get(v.visitKey) ?? null,
      })),
    // 원자료 표: 방문에 대응된 결과 행 전부 (확정값만 옵션은 이미 반영됨)
    getLongRows: () => longRows,
    visitLabelOf,
    visitRank: (visitKey) => {
      const [base, seq] = String(visitKey).split('#');
      return (visitOrder.get(base) ?? 999) * 10000 + Number(seq ?? 0);
    },
    maxRepeat: (repeatKey, subjectIds) => {
      const m = repeatCounts.get(repeatKey);
      if (!m) return 0;
      return subjectIds.reduce((max, id) => Math.max(max, m.get(id) ?? 0), 0);
    },
    maxBlockCount: (blockName, visitKey, subjectIds) =>
      subjectIds.reduce((max, id) => Math.max(max, blocks.get(blockName)?.get(id)?.get(visitKey)?.length ?? 0), 0),
    hasTestCode: (code) => dictionaryTestCodes.size === 0 || dictionaryTestCodes.has(code),
    hasCriteria: (key) => criteriaCatalog.byKey.size === 0 || criteriaCatalog.byKey.has(String(key).toUpperCase()),
    hasSurveyItem: (surveyCode, itemKey) => surveyCatalog.byRef.size === 0 || surveyCatalog.byRef.has(surveyRef(surveyCode, itemKey)),
    hasProtocolVisit: (regimen, cycle) => dictionaryVisits.size === 0 || dictionaryVisits.has(`${regimen}|${cycle ?? 0}`),
    stats,
  };
}
